<template>
  <!-- On mobile -->
  <RevDivider class="relative -inset-x-[24px] w-[calc(100%+48px)] md:hidden" />
  <div class="my-24 space-y-16 md:hidden">
    <div class="body-1-bold">{{ i18n(translations.paymentHistoryTitle) }}</div>

    <ul class="divide-static-default-low space-y-12 divide-y py-12">
      <li
        v-for="paymentOccurence in paymentHistoryFourFirstOccurences"
        :key="paymentOccurence.date"
      >
        <div class="mt-12 grid grid-cols-3">
          <div class="text-static-default-low body-2">
            {{ getStatusLabel(paymentOccurence.status) }}
          </div>
          <div class="text-static-default-low body-2 text-center">
            {{
              i18n.date(new Date(paymentOccurence.date), INSURANCE_DATE_FORMAT)
            }}
          </div>
          <div class="body-2 text-right">
            {{ i18n.price(paymentOccurence.price) }}
          </div>
        </div>
      </li>
    </ul>

    <div class="flex justify-center">
      <RevLink
        v-if="isPaymentHistoryLongerThanFour"
        class="body-1-link"
        :tracking="{
          zone: TRACKING_ZONES.POLICY_DETAILS,
          name: 'see-payment-history',
        }"
        @click="openModal(MODAL_NAMES.PAYMENT_HISTORY_DRAWER)"
        >{{ i18n(translations.seePaymentHistory) }}</RevLink
      >
    </div>

    <RevDialog
      :name="MODAL_NAMES.PAYMENT_HISTORY_DRAWER"
      :title="i18n(translations.paymentHistoryDrawerTitle)"
    >
      <template #body>
        <ul class="divide-static-default-low space-y-12 divide-y">
          <li
            v-for="paymentOccurence in paymentHistory"
            :key="paymentOccurence.date"
          >
            <div class="mt-12 grid grid-cols-3">
              <div class="text-static-default-low body-2">
                {{ getStatusLabel(paymentOccurence.status) }}
              </div>
              <div class="text-static-default-low body-2 text-center">
                {{
                  i18n.date(
                    new Date(paymentOccurence.date),
                    INSURANCE_DATE_FORMAT,
                  )
                }}
              </div>
              <div class="body-2 text-right">
                {{ i18n.price(paymentOccurence.price) }}
              </div>
            </div>
          </li>
        </ul>
      </template>
    </RevDialog>
  </div>

  <!-- On desktop -->
  <div class="hidden md:block">
    <RevDivider class="relative -inset-x-[32px] w-[calc(100%+64px)]" />
    <RevAccordionItem
      @click="
        track.trackClick({
          name: 'open-payment-history-accordion',
          zone: TRACKING_ZONES.POLICY_DETAILS,
        })
      "
    >
      <template #title>
        <div class="body-1">{{ i18n(translations.paymentHistoryTitle) }}</div>
      </template>
      <template #body>
        <ul class="divide-static-default-low space-y-12 divide-y">
          <li
            v-for="paymentOccurence in paymentHistory"
            :key="paymentOccurence.date"
          >
            <div class="mt-12 grid grid-cols-3">
              <div class="text-static-default-low body-2">
                {{ getStatusLabel(paymentOccurence.status) }}
              </div>
              <div class="text-static-default-low body-2 text-center">
                {{
                  i18n.date(
                    new Date(paymentOccurence.date),
                    INSURANCE_DATE_FORMAT,
                  )
                }}
              </div>
              <div class="body-2 text-right">
                {{ i18n.price(paymentOccurence.price) }}
              </div>
            </div>
          </li>
        </ul>
      </template>
    </RevAccordionItem>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevDialog } from '@ds/components/Dialog'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'

import {
  INSURANCE_DATE_FORMAT,
  MODAL_NAMES,
  TRACKING_ZONES,
} from '~/scopes/dashboard/services-hub/constant'
import type { PolicyDetails } from '~/scopes/dashboard/services-hub/types'

import translations from './PaymentHistory.translations'

const i18n = useI18n()
const track = useTracking()

type PaymentHistory = PolicyDetails['paymentHistory']

const props = defineProps<{
  paymentHistory: PaymentHistory
}>()

function getStatusLabel(status: PaymentHistory[number]['status']): string {
  const statusLabel = {
    SUCCEEDED: i18n(translations.succeeded),
    CHARGEBACK_DONE: i18n(translations.chargebackDone),
    FAILED: i18n(translations.failed),
    REFUNDED: i18n(translations.refunded),
  }

  return statusLabel[status]
}

const paymentHistoryFourFirstOccurences = computed(() =>
  props.paymentHistory.slice(0, 4),
)

const isPaymentHistoryLongerThanFour = computed(
  () => props.paymentHistory.length > 4,
)
</script>
